import type { ImageRef, LinkItem } from './types';

export interface AboutFace {
  name: string;
  image: ImageRef & {
    width: number;
    height: number;
    srcSet?: Array<{ src: string; width: number }>;
  };
  /** Stagger delay class matching Webflow `.about-face-card.cc-*` animation. */
  delayMs: number;
  /** Featured founder card (Amer) — larger type and padding. */
  featured?: boolean;
}

export interface AboutFaceColumn {
  variant: 'default' | 'sec' | 'main';
  faces: AboutFace[];
}

export const aboutHero = {
  title: 'A global account for the rest of us.',
  cta: { label: 'Download', href: '/get-the-app' } satisfies LinkItem,
};

/** Five-column face grid from the Webflow About hero (left → right). */
export const aboutFaceColumns: AboutFaceColumn[] = [
  {
    variant: 'default',
    faces: [
      {
        name: 'Émanuel Grenier-Benoit',
        image: {
          src: '/images/about/1.webp',
          alt: 'Émanuel Grenier-Benoit',
          width: 380,
          height: 368,
        },
        delayMs: 600,
      },
      {
        name: 'Abdallah AbuHashem',
        image: {
          src: '/images/about/2.webp',
          alt: 'Abdallah AbuHashem',
          width: 380,
          height: 368,
        },
        delayMs: 700,
      },
    ],
  },
  {
    variant: 'sec',
    faces: [
      {
        name: 'Marie Hubulova',
        image: {
          src: '/images/about/3.webp',
          alt: 'Marie Hubulova',
          width: 380,
          height: 368,
        },
        delayMs: 300,
      },
      {
        name: 'Abdul Gofur',
        image: {
          src: '/images/about/4.webp',
          alt: 'Abdul Gofur',
          width: 380,
          height: 368,
        },
        delayMs: 500,
      },
    ],
  },
  {
    variant: 'main',
    faces: [
      {
        name: 'Amer Baroudi',
        image: {
          src: '/images/about/5.webp',
          alt: 'Amer Baroudi',
          width: 560,
          height: 540,
          srcSet: [
            { src: '/images/about/5-p-500.webp', width: 500 },
            { src: '/images/about/5.webp', width: 561 },
          ],
        },
        delayMs: 200,
        featured: true,
      },
    ],
  },
  {
    variant: 'sec',
    faces: [
      {
        name: 'Mira Hoteit',
        image: {
          src: '/images/about/6.webp',
          alt: 'Mira Hoteit',
          width: 380,
          height: 368,
        },
        delayMs: 300,
      },
      {
        name: 'Youssef Ramzy',
        image: {
          src: '/images/about/7.webp',
          alt: 'Youssef Ramzy',
          width: 380,
          height: 368,
        },
        delayMs: 500,
      },
    ],
  },
  {
    variant: 'default',
    faces: [
      {
        name: 'Gaelle Yammine',
        image: {
          src: '/images/about/8.webp',
          alt: 'Gaelle Yammine',
          width: 380,
          height: 368,
        },
        delayMs: 600,
      },
      {
        name: 'Zaid Kaddour',
        image: {
          src: '/images/about/9.webp',
          alt: 'Zaid Kaddour',
          width: 380,
          height: 368,
        },
        delayMs: 700,
      },
    ],
  },
];

export const aboutIntro = {
  paragraphs: [
    'Like so many others, our founders escaped a struggling economy.',
    'We work for a world in which everyone has access to a trusted account.',
  ],
  logos: [
    {
      src: '/images/about/financial_times_logo.png',
      alt: 'Financial Times',
      width: 251,
      height: 20,
    },
    {
      src: '/images/about/techcrunch_logo.png',
      alt: 'TechCrunch',
      width: 41,
      height: 20,
    },
    {
      src: '/images/about/fintech-news-logo.png',
      alt: 'Fintech News',
      width: 111,
      height: 32,
    },
  ],
};

export const aboutMeetTeam = {
  title: 'Meet the team',
  paragraphs: [
    'nsave is on a mission to democratise safe and secure financial services for all.',
    'The team has experienced financial exclusion firsthand, and is driven by a strong desire to empower its users.',
    'Join us as we shift the financial sector from exclusion to action.',
    'This is your story and ours.',
  ],
  image: {
    src: '/images/about/nsave_team_060825-20-1-.jpg',
    alt: 'Picture of the nsave team',
    srcSet: [
      { src: '/images/about/nsave_team_060825-20-1-p-500.jpg', width: 500 },
      { src: '/images/about/nsave_team_060825-20-1-p-800.jpg', width: 800 },
      { src: '/images/about/nsave_team_060825-20-1-p-1080.jpg', width: 1080 },
      { src: '/images/about/nsave_team_060825-20-1-p-1600.jpg', width: 1600 },
      { src: '/images/about/nsave_team_060825-20-1-p-2000.jpg', width: 2000 },
      { src: '/images/about/nsave_team_060825-20-1-.jpg', width: 2080 },
    ],
  } satisfies ImageRef & {
    srcSet: Array<{ src: string; width: number }>;
  },
};

export const aboutCareersCta = {
  title: 'Work at nsave',
  body: "We're looking for the right people to help us do it. If you're passionate about making change in the world, this might be the place for you.",
  cta: { label: 'See open positions', href: '/careers' } satisfies LinkItem,
};

export const aboutMeta = {
  title: 'About nsave | Global USD Accounts',
  description:
    'Learn about nsave, a global fintech platform providing USD accounts, ACH routing, and cross-border payments for freelancers and remote workers.',
};
